/*
  Tudaloo cart drawer actions — discount codes, one-tap add (Go / In-Cistern Kit),
  and the Pro → sensor-lid variant swap.

  Every action asks Shopify for the 'cart-drawer' + 'cart-icon-bubble' sections in the
  same request and swaps .drawer__inner exactly like Refresh's CartDrawerItems does,
  so the native quantity/remove controls never fall out of sync.
*/
(function () {
  if (window.TudalooCartDrawer) return;

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  let busy = false;

  function jsonPost(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => response.json().then((data) => ({ ok: response.ok, data })));
  }

  function withSections(body) {
    return Object.assign({}, body, { sections: SECTIONS, sections_url: window.location.pathname });
  }

  function parse(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  function render(state) {
    const sections = state && state.sections;
    const drawer = document.querySelector('cart-drawer');
    const current = document.querySelector('#CartDrawer .drawer__inner');
    const fresh = sections && parse(sections['cart-drawer'], '.drawer__inner');

    if (!fresh || !current) {
      // Section HTML missing — never pretend it worked; reload the cart instead.
      window.location.href = window.routes ? window.routes.cart_url : '/cart';
      return false;
    }
    current.innerHTML = fresh.innerHTML;
    if (drawer && typeof state.item_count === 'number') {
      drawer.classList.toggle('is-empty', state.item_count === 0);
    }

    const bubble = document.getElementById('cart-icon-bubble');
    const freshBubble = sections && parse(sections['cart-icon-bubble'], '.shopify-section');
    if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;

    // Same source tag Refresh's own cart controls use, so <cart-drawer-items> doesn't re-fetch.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    }
    return true;
  }

  function errorText(data) {
    return (data && (data.description || data.message)) || (window.cartStrings && window.cartStrings.error) ||
      'Something went wrong. Please try again.';
  }

  function showError(host, message) {
    const el = host && host.querySelector('.tdc-action__error, .tdc-disc__error');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  function setLoading(button, loading, label) {
    if (!button) return;
    if (loading) {
      button.dataset.label = button.textContent;
      button.textContent = label || 'Adding…';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      if (button.dataset.label) button.textContent = button.dataset.label;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /* ---------- One-tap add (Go card, In-Cistern Kit) ---------- */
  class TdcAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.add());
    }

    add() {
      if (busy) return;
      busy = true;
      showError(this, '');
      setLoading(this.button, true);
      jsonPost(`${window.routes.cart_add_url}.js`, withSections({ items: [{ id: Number(this.dataset.variant), quantity: 1 }] }))
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            setLoading(this.button, false);
            showError(this, errorText(data));
            return;
          }
          render(data);
        })
        .catch(() => {
          setLoading(this.button, false);
          showError(this, errorText());
        })
        .finally(() => {
          busy = false;
        });
    }
  }

  /* ---------- Pro → Pro Plus (sensor lid) swap ---------- */
  class TdcSwap extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.swap());
    }

    swap() {
      if (busy) return;
      busy = true;
      showError(this, '');
      setLoading(this.button, true, 'Upgrading…');
      const qty = Number(this.dataset.qty) || 1;

      // Add the upgrade first so a failed add never leaves the customer with nothing.
      jsonPost(`${window.routes.cart_add_url}.js`, { items: [{ id: Number(this.dataset.to), quantity: qty }] })
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(errorText(data));
          return jsonPost(`${window.routes.cart_change_url}.js`, withSections({ id: this.dataset.lineKey, quantity: 0 }));
        })
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(errorText(data));
          render(data);
        })
        .catch((error) => {
          setLoading(this.button, false);
          showError(this, error.message || errorText());
          refreshDrawer();
        })
        .finally(() => {
          busy = false;
        });
    }
  }

  /* ---------- Discount codes (real Shopify discounts via /cart/update.js) ---------- */
  class TdcDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input');
      this.button = this.querySelector('.tdc-disc__btn');
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.querySelectorAll('[data-remove-code]').forEach((button) =>
        button.addEventListener('click', () => this.remove(button.dataset.removeCode))
      );
    }

    get codes() {
      return (this.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
    }

    onSubmit(event) {
      event.preventDefault();
      const code = this.input.value.trim();
      if (!code || busy) return;
      if (this.codes.some((c) => c.toLowerCase() === code.toLowerCase())) {
        showError(this, `${code} is already applied.`);
        return;
      }
      this.apply(code);
    }

    apply(code) {
      busy = true;
      showError(this, '');
      setLoading(this.button, true, 'Applying…');
      const existing = this.codes;

      jsonPost(`${window.routes.cart_update_url}.js`, withSections({ discount: existing.concat(code).join(',') }))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(errorText(data));
          const match = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
          if (match && match.applicable) {
            render(data);
            return null;
          }
          // Not applicable — take it back off so the cart stays clean, then explain.
          return jsonPost(`${window.routes.cart_update_url}.js`, withSections({ discount: existing.join(',') })).then(
            ({ data: reverted }) => {
              render(reverted);
              const host = document.querySelector('cart-drawer tdc-discount');
              if (host) {
                host.querySelector('input').value = code;
                showError(host, `${code} can't be applied to this cart.`);
              }
            }
          );
        })
        .catch((error) => {
          setLoading(this.button, false);
          showError(this, error.message || errorText());
        })
        .finally(() => {
          busy = false;
        });
    }

    remove(code) {
      if (busy) return;
      busy = true;
      const remaining = this.codes.filter((c) => c.toLowerCase() !== String(code).toLowerCase());
      jsonPost(`${window.routes.cart_update_url}.js`, withSections({ discount: remaining.join(',') }))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(errorText(data));
          render(data);
        })
        .catch((error) => showError(this, error.message || errorText()))
        .finally(() => {
          busy = false;
        });
    }
  }

  function refreshDrawer() {
    const url = `${window.routes.cart_url}?sections=${SECTIONS.join(',')}`;
    return fetch(url, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((sections) => {
        // Background refresh: if the section can't be rendered, leave the drawer as it is.
        if (!sections || !sections['cart-drawer']) return null;
        return fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => render({ sections, item_count: cart.item_count }));
      })
      .catch(() => {});
  }

  /* Refresh's <cart-drawer-items> inherits CartItems.onCartUpdate, which swaps the drawer's
     contents for the /cart page's markup whenever anything else (an app, a quick-add) publishes
     a cart update. Re-render the drawer itself instead. */
  customElements.whenDefined('cart-drawer-items').then(() => {
    const DrawerItems = customElements.get('cart-drawer-items');
    if (DrawerItems && !DrawerItems.prototype.__tdcPatched) {
      DrawerItems.prototype.onCartUpdate = function () {
        return refreshDrawer();
      };
      DrawerItems.prototype.__tdcPatched = true;
    }
  });

  /* Captain Protection always injects its widget just above Checkout. Move it up under the
     discount field so the fee is always visible in the summary (it's re-injected after every
     drawer re-render, so this watches for it). Captain keeps working once moved.

     Freeze-safety (Oct 23): Captain runs its own whole-page MutationObserver. Never fight it —
     we don't rewrite any of Captain's text (the short description is CSS-only), we react at
     most once per 30ms (never synchronously), we pause our own observer while we move nodes, and a
     circuit breaker stops moving the widget if it ever churns (it then simply stays where
     Captain put it, still fully working). */
  let protectionObserver = null;
  let placeQueued = false;
  let placeRuns = 0;
  let placeWindowStart = 0;
  let placePaused = false;

  function schedulePlaceProtection() {
    if (placeQueued || placePaused) return;
    placeQueued = true;
    // A short timer (not a microtask) so the page always gets to handle taps and scrolls
    // between reactions; also not requestAnimationFrame, which some browsers pause.
    setTimeout(() => {
      placeQueued = false;
      placeProtection();
    }, 30);
  }

  function placeProtection() {
    const now = performance.now();
    if (now - placeWindowStart > 2000) {
      placeWindowStart = now;
      placeRuns = 0;
    }
    if (++placeRuns > 40) {
      placePaused = true;
      if (protectionObserver) protectionObserver.disconnect();
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    const slot = drawer && drawer.querySelector('[data-tdc-protect-slot]');
    if (slot) {
      // Captain updates the copy it injected most recently (the one outside our slot), so keep that one.
      const outside = Array.from(drawer.querySelectorAll('.ship-insurance')).filter((w) => w.parentElement !== slot);
      if (outside.length) {
        if (protectionObserver) protectionObserver.disconnect();
        const fresh = outside[outside.length - 1];
        slot.replaceChildren(fresh);
        outside.slice(0, -1).forEach((w) => w.remove());
        if (protectionObserver) protectionObserver.observe(drawer, { childList: true, subtree: true });
      }
    }
    updateProtectionTotal();
  }

  /* Captain only adds its fee to the cart when Checkout is clicked, so mirror it in the
     summary while the toggle is on: show a "Shipping protection" row and add it to the total,
     so the drawer total matches what checkout will charge. */
  const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function updateProtectionTotal() {
    const drawer = document.querySelector('cart-drawer');
    const row = drawer && drawer.querySelector('[data-tdc-protect-row]');
    const total = drawer && drawer.querySelector('[data-tdc-total]');
    if (!row || !total) return;
    const toggle = drawer.querySelector('.tdc-protect #tm_switch_btn');
    const priceEl = drawer.querySelector('.tdc-protect .tm_price');
    const fee = priceEl ? Math.round(parseFloat(priceEl.textContent.replace(/[^0-9.]/g, '')) * 100) || 0 : 0;
    const on = Boolean(toggle && toggle.checked && fee > 0);
    const base = Number(total.dataset.base) || 0;

    if (row.hidden === on) row.hidden = !on;
    setText(row.querySelector('[data-tdc-protect-amount]'), money.format(fee / 100));
    setText(total, money.format((base + (on ? fee : 0)) / 100));
  }

  function watchProtection() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !window.MutationObserver) return;
    protectionObserver = new MutationObserver(schedulePlaceProtection);
    protectionObserver.observe(drawer, { childList: true, subtree: true });
    drawer.addEventListener('change', (event) => {
      if (event.target && event.target.id === 'tm_switch_btn') setTimeout(updateProtectionTotal, 0);
    });
    drawer.addEventListener('click', (event) => {
      if (event.target && event.target.closest && event.target.closest('.tdc-protect')) {
        setTimeout(updateProtectionTotal, 50);
        setTimeout(updateProtectionTotal, 500);
      }
    });
    placeProtection();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchProtection);
  else watchProtection();

  if (!customElements.get('tdc-add')) customElements.define('tdc-add', TdcAdd);
  if (!customElements.get('tdc-swap')) customElements.define('tdc-swap', TdcSwap);
  if (!customElements.get('tdc-discount')) customElements.define('tdc-discount', TdcDiscount);

  window.TudalooCartDrawer = { render, refresh: refreshDrawer };
})();
